import React, { useState, useMemo, useCallback } from 'react'
import { View, SectionList, ActivityIndicator } from 'react-native'
import { useFocusEffect, useTheme } from '@react-navigation/native'
import { Screen, Text, Header } from '@components'
import { NotificationItem } from './components/NotificationItem'
import { NotificationTabs, type NotificationTabKey } from './components/NotificationTabs'
import { NotificationSkeleton } from './components/NotificationSkeleton'
import { NotificationsEmptyState } from './components/NotificationsEmptyState'
import getStyle from './style'
import dayjs from 'dayjs'
import {
  usePaginateNotifications,
  NotificationType,
  ContactRequestAnswer,
  type Notification as ApiNotification,
} from '../../hooks/api/queries'
import { useAnswerRequestedContacts, useMarkNotificationAsRead } from '../../hooks/api/mutations'
import { useLanguage } from '@context'
import { Scope, groupNotificationsByDate, type NotificationDateGroup } from '@utils'

// Type for formatted notifications with additional properties
type FormattedNotification = ApiNotification & {
  message: string
  contactId?: string
  contactIds?: string[]
  contactRequestId?: string
  accepted?: boolean
  isRead: boolean
  description?: string
}

const SECTION_LABEL_KEYS: Record<NotificationDateGroup, string> = {
  today: 'notifications.sections.today',
  thisWeek: 'notifications.sections.thisWeek',
  earlier: 'notifications.sections.earlier',
}

export const Notifications: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const [activeTab, setActiveTab] = useState<NotificationTabKey>('all')

  // Fetch notifications from API
  const {
    flatData: apiNotifications = [],
    isFetchingNextPage: isFetchingNextPageNotifications,
    hasNextPage: hasNextPageNotifications,
    fetchNextPage: fetchNextPageNotifications,
    pageMetadata,
    refetch: refetchNotifications,
    isLoading,
    isPlaceholderData,
  } = usePaginateNotifications(activeTab)

  const { mutate: answerContactRequest } = useAnswerRequestedContacts()
  const { mutate: markAsRead } = useMarkNotificationAsRead()

  useFocusEffect(
    useCallback(() => {
      void refetchNotifications()
    }, [refetchNotifications]),
  )

  // Filter and format notifications by type
  const formattedNotifications = useMemo((): FormattedNotification[] => {
    return apiNotifications.map((notification: ApiNotification): FormattedNotification => {
      const isRead = notification.readAt !== null

      // Format message based on notification type
      let message = ''
      let contactId: string | undefined
      let contactIds: string[] | undefined
      let contactRequestId: string | undefined
      let accepted: boolean | undefined
      let description: string | undefined

      if (notification.type === NotificationType.CONTACT_REQUEST_CREATED) {
        const data = notification.data as { contactRequestId: string; senderId: string }
        message = t('notifications.friendRequest')
        contactId = data.senderId
        contactRequestId = data.contactRequestId
      } else if (notification.type === NotificationType.CONTACT_REQUEST_ANSWERED) {
        const data = notification.data as {
          contactRequestId: string
          recipientId: string
          answer: ContactRequestAnswer
        }
        message =
          data.answer === ContactRequestAnswer.CONFIRMED
            ? t('notifications.acceptedRequest')
            : t('notifications.rejectedRequest')
        contactId = data.recipientId
        contactRequestId = data.contactRequestId
        accepted = data.answer === ContactRequestAnswer.CONFIRMED
      } else if (notification.type === NotificationType.CALENDAR_EVENT_UPCOMING) {
        const data = notification.data as {
          calendarEventId: string
          startDateTime: string
          participantAccountIds?: string[]
        }
        message = t('notifications.upcomingEvent')
        contactIds = data.participantAccountIds
        description = dayjs(data.startDateTime).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.KEEP_CONTACT_REMINDER) {
        const data = notification.data as {
          keepContactId: string
          contactId: string
          keepDate: string
        }
        message = t('notifications.contactReminder')
        contactId = data.contactId
        description = dayjs(data.keepDate).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.SCHEDULED_MESSAGE_UPCOMING) {
        const data = notification.data as {
          scheduledMessageId: string
          contactId: string
          scheduledAt: string
        }
        message = t('notifications.scheduledMessage')
        contactId = data.contactId
        description = dayjs(data.scheduledAt).format('DD/MM/YYYY HH:mm')
      } else if (notification.type === NotificationType.BIRTHDAY_UPCOMING) {
        const data = notification.data as { contactId: string; birthDate: string }
        message = t('notifications.birthday')
        contactId = data.contactId
        description = dayjs(data.birthDate).format('DD/MM/YYYY')
      }

      return {
        ...notification,
        message,
        contactId,
        contactIds,
        contactRequestId,
        accepted,
        isRead,
        description,
      }
    })
  }, [apiNotifications, t])

  // Break the flat list into "today / this week / earlier" sections.
  const sections = useMemo(
    () =>
      groupNotificationsByDate(formattedNotifications).map((section) => ({
        key: section.key,
        title: t(SECTION_LABEL_KEYS[section.key]),
        data: section.data,
      })),
    [formattedNotifications, t],
  )

  // NOTE: Filtering is done server-side via the `tab` query param.
  // This list already contains the correct notifications for the active tab.
  const allCount =
    pageMetadata?.countsByTab?.all?.unread ?? formattedNotifications.filter((n) => !n.isRead).length
  const requestsCount = pageMetadata?.countsByTab?.requests?.unread ?? 0
  const agendaCount = pageMetadata?.countsByTab?.agenda?.unread ?? 0

  const tabs = useMemo(
    () => [
      { key: 'all' as const, label: t('notifications.all'), count: allCount },
      { key: 'requests' as const, label: t('notifications.requests'), count: requestsCount },
      { key: 'agenda' as const, label: t('notifications.agenda'), count: agendaCount },
    ],
    [t, allCount, requestsCount, agendaCount],
  )

  const handleAccept = (contactRequestId: string) => {
    answerContactRequest({
      requestedContactId: contactRequestId,
      answer: 'CONFIRM',
      scope: Scope.PRIVATE,
    })
  }

  const handleReject = (contactRequestId: string) => {
    answerContactRequest({
      requestedContactId: contactRequestId,
      answer: 'DECLINE',
    })
  }

  const handleRead = (notification: FormattedNotification) => {
    if (!notification.isRead) {
      markAsRead(notification.id)
    }
  }

  const renderNotificationItem = ({ item }: { item: FormattedNotification }) => (
    <NotificationItem
      notification={item}
      onPress={() => handleRead(item)}
      onAccept={() => item.contactRequestId && handleAccept(item.contactRequestId)}
      onReject={() => item.contactRequestId && handleReject(item.contactRequestId)}
    />
  )

  return (
    <Screen>
      <Header title={t('tabNavigation.notifications')} iconRight="threePoints" />

      <View style={styles.body}>
        <NotificationTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

        {isLoading ? (
          <NotificationSkeleton />
        ) : (
          // Dim slightly while a tab switch / push refetch loads the next list,
          // so the previous content stays put instead of flashing blank.
          <View style={[styles.listWrapper, isPlaceholderData && styles.listLoading]}>
            <SectionList
              sections={sections}
              renderItem={renderNotificationItem}
              keyExtractor={(item) => item.id}
              renderSectionHeader={({ section }) => (
                <Text
                  type="bodySmallBold"
                  color={theme.colors.greyBlue}
                  style={styles.sectionHeader}
                >
                  {section.title}
                </Text>
              )}
              stickySectionHeadersEnabled={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.listContent}
              ListEmptyComponent={<NotificationsEmptyState />}
              onEndReached={() => {
                if (hasNextPageNotifications && !isFetchingNextPageNotifications) {
                  void fetchNextPageNotifications()
                }
              }}
              onEndReachedThreshold={0.5}
              ListFooterComponent={
                isFetchingNextPageNotifications ? (
                  <ActivityIndicator color={theme.colors.primary} style={styles.footerLoader} />
                ) : null
              }
            />
          </View>
        )}
      </View>
    </Screen>
  )
}
